import { useCallback, useState } from "react";
import { DocumentChangeAuditRepositoryImpl } from "../../data/repositories/DocumentChangeAuditRepositoryImpl";
import {
  GetDocumentChangeAuditsUseCase,
  GetDocumentChangeReportSummaryUseCase,
} from "../../domain/use-cases/document-change-audit";
import type {
  DocumentChangeAuditEntity,
  DocumentChangeAuditQueryParams,
  DocumentChangeReportSummaryEntity,
} from "../../domain/entities/DocumentChangeAuditEntity";

const repository = new DocumentChangeAuditRepositoryImpl();
const getAuditsUseCase = new GetDocumentChangeAuditsUseCase(repository);
const getSummaryUseCase = new GetDocumentChangeReportSummaryUseCase(repository);

export interface UseDocumentChangeReportsReturn {
  audits: DocumentChangeAuditEntity[];
  summary: DocumentChangeReportSummaryEntity | null;
  loading: boolean;
  error: Error | null;
  pagination: {
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
  };
  fetchReports: (params?: DocumentChangeAuditQueryParams) => Promise<void>;
  refreshSummary: (params?: DocumentChangeAuditQueryParams) => Promise<void>;
}

export const useDocumentChangeReports = (): UseDocumentChangeReportsReturn => {
  const [audits, setAudits] = useState<DocumentChangeAuditEntity[]>([]);
  const [summary, setSummary] =
    useState<DocumentChangeReportSummaryEntity | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [pagination, setPagination] = useState({
    total: 0,
    limit: 25,
    offset: 0,
    hasMore: false,
  });

  const fetchReports = useCallback(
    async (params?: DocumentChangeAuditQueryParams) => {
      try {
        setLoading(true);
        setError(null);
        const result = await getAuditsUseCase.execute({
          limit: 25,
          offset: 0,
          ...params,
        });
        setAudits((current) =>
          (params?.offset ?? 0) > 0
            ? [...current, ...result.audits]
            : result.audits,
        );
        setSummary(result.summary);
        setPagination(result.pagination);
      } catch (err) {
        setError(
          err instanceof Error
            ? err
            : new Error("Failed to fetch document change reports"),
        );
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const refreshSummary = useCallback(
    async (params?: DocumentChangeAuditQueryParams) => {
      try {
        const result = await getSummaryUseCase.execute(params);
        setSummary(result);
      } catch (err) {
        setError(
          err instanceof Error
            ? err
            : new Error("Failed to fetch report summary"),
        );
        throw err;
      }
    },
    [],
  );

  return {
    audits,
    summary,
    loading,
    error,
    pagination,
    fetchReports,
    refreshSummary,
  };
};
